import {
  AGE,
  CONTENT_TYPE,
  GENDER,
  PUBLIC_FILTER_TYPE,
  RACE,
  SOURCE_PLATFORM_TYPE,
} from '@/entities/public-filter/public-filter-constants';
import { useResetStorage } from '@/shared/hooks/use-reset';
import { useAtom } from 'jotai';
import { atomFamily, atomWithStorage } from 'jotai/utils';
import { useCallback, useMemo } from 'react';
import type { PublicFilterValue } from './public-filter-menu';

export type PublicFilter = {
  keyword: string;
  contentType: CONTENT_TYPE[];
  platform: SOURCE_PLATFORM_TYPE[];
  gender: GENDER[];
  race: RACE[];
  age: AGE[];
  location: string[];
};

export const INSIGHT_FILTER_STORAGE_KEY = 'public-insight-filter';
const INSIGHT_FILTER_STORAGE_RESET_KEY = 'public-insight-filter-reset';
const INSIGHT_FILTER_STORAGE_RESET_VALUE = '251215';

const getStorageKey = (query: string | null) => {
  return `${INSIGHT_FILTER_STORAGE_KEY}-${query}`;
};

const dynamicStorageAtomFamily = atomFamily((key: string | null) =>
  atomWithStorage<PublicFilterValue[]>(getStorageKey(key), []),
);

export const usePublicFilter = (query: string | null) => {
  const filterAtom = dynamicStorageAtomFamily(query);
  const [filterValues, setFilterValues] = useAtom(filterAtom);

  useResetStorage({
    resetKey: INSIGHT_FILTER_STORAGE_RESET_KEY,
    resetValue: INSIGHT_FILTER_STORAGE_RESET_VALUE,
    storageKeysToReset: [getStorageKey(query)],
  });

  const onKeywordFilterChange = useCallback(
    (keyword: string) => {
      setFilterValues((prev) => {
        const isExist = prev.some((v) => v.filterType === PUBLIC_FILTER_TYPE.KEYWORD);
        if (isExist) {
          return prev.map((v) =>
            v.filterType === PUBLIC_FILTER_TYPE.KEYWORD ? { ...v, value: keyword } : v,
          );
        } else {
          return [...prev, { filterType: PUBLIC_FILTER_TYPE.KEYWORD, value: keyword }];
        }
      });
    },
    [setFilterValues],
  );

  const publicFilter = useMemo(() => buildPublicFilter(filterValues), [filterValues]);

  const setFilterValueHandler = useCallback(
    (values: PublicFilterValue[]) => {
      // Filter out duplicate entries where filterType and value are identical
      const uniqueValues = values.filter(
        (value, index, self) =>
          index ===
          self.findIndex((v) => v.filterType === value.filterType && v.value === value.value),
      );
      setFilterValues(uniqueValues);
    },
    [setFilterValues],
  );

  return {
    filterValues,
    publicFilter,
    setFilterValues: setFilterValueHandler,
    onKeywordFilterChange,
  };
};

const buildPublicFilter = (values: PublicFilterValue[]): PublicFilter => {
  return values.reduce(
    (acc: PublicFilter, value: PublicFilterValue) => {
      switch (value.filterType) {
        case PUBLIC_FILTER_TYPE.PROMOTION_TYPE:
          acc.contentType.push(value.value as CONTENT_TYPE);
          break;
        case PUBLIC_FILTER_TYPE.PLATFORM:
          acc.platform.push(value.value as SOURCE_PLATFORM_TYPE);
          break;
        case PUBLIC_FILTER_TYPE.GENDER:
          acc.gender.push(value.value as GENDER);
          break;
        case PUBLIC_FILTER_TYPE.RACE:
          acc.race.push(value.value as RACE);
          break;
        case PUBLIC_FILTER_TYPE.AGE:
          acc.age.push(value.value as AGE);
          break;
        case PUBLIC_FILTER_TYPE.LOCATION:
          acc.location.push(value.value as string);
          break;
        case PUBLIC_FILTER_TYPE.KEYWORD:
          acc.keyword = value.value as string;
          break;
      }
      return acc;
    },
    {
      keyword: '',
      contentType: [],
      platform: [],
      gender: [],
      race: [],
      age: [],
      location: [],
    },
  );
};
